/* eslint-disable complexity */
import './OfferCardMini.css';

/* eslint-disable padding-line-between-statements */
import React, { useCallback, useRef, useState } from 'react';

import classname from '@search/classname/src';
import { errorCatcherWrap } from '@search/error/src/catcher';
import { Currency } from '@search/filter-enums/enums/Currency';
import Paper from '@search/vtbeco-ui/src/components/Paper';
import { SizeEnum } from '@search/vtbeco-ui/src/types';
import { Badge as DSBadge } from '@vtblife/uikit';
import { FavoritesButton } from '@search/vtbeco-frontend-core/view/common/components/FavoritesButton/FavoritesButton';
import { AgentFavoritesModal } from '@search/classified-common/src/Agent/AgentFavoritesDropdown/AgentFavoritesModal';
import {
    getAuthorDescription
} from '@search/vtbeco-frontend-core/view/common/components/AuthorBadge/AuthorBadge';
import { joinComponents } from '@search/classified-common/src/joinComponents';
import { ClClickableAddress } from '@search/classified-common/src/ClClickableAddress/ClClickableAddress';

import { useM2AuthContext } from '@search/auth/src/M2AuthProvider';
import badgeLabel from '../../../../dict/offer-badge';
import { ClassifiedAnalytics } from '../../../../domain/offer/analytics/ClassifiedAnalytics';
import { useClassifiedOfferDataLayerPush } from '../../../../domain/offer/analytics/ClassifiedOfferGtmContext';
import {
    useHandyAddressCompat,
    useOfferVisited,
    dealTypePeriodCompat, OfferSnippetModel, priceCompat, useOfferCardLink, useOfferCompat, useOfferRouteListCompat
} from '../../../../domain/offer/search/page/SearchOffersQuery';
import { useOfferViewAnalytics } from '../../hooks/useOfferViewAnalytics';
import { useFavoritesButtonState } from '../FavoritesButton/useFavoritesButtonState';
import { GalleryMobile2 } from '../gallery/GalleryMobile2/GalleryMobile2';
import { OfferRouteCard } from '../OfferRouteCard';
import Price from '../Price';
import { useSubscribeToPriceModalState } from '../SubscribeToPriceChange/useSubscribeToPriceModalState';
import { useSubscribeToPriceChange } from '../SubscribeToPriceChange/useSubscribeToPriceChange';
import { SubscribeToPriceModal } from '../SubscribeToPriceChange/SubscribeToPriceModal';
import { SubscribeToPriceChange } from '../SubscribeToPriceChange';
import { ImageLazySrcSetProvide, useClImageLazySrcSetUrl } from '../../../../domain/image/lazy/ImageLazySrcSet';

const cn = classname.bind(null, 'OfferCardMini');

/* eslint-disable complexity */
export const OfferCardMini = React.memo(errorCatcherWrap(function OfferCardMini({
    model,
    noLink,
    children,
    gtmIdFavoriteClick,
    placement,
    forceFavoriteUpdate
}: {
    forceFavoriteUpdate?(): void;
    gtmIdFavoriteClick?: string;
    noLink?: boolean;
    placement: 'map' | 'modal';
    model: OfferSnippetModel;
    children?: React.ReactNode;
}) {
    const realtyObject = model.realtyObject;
    const dealType = model.dealType;
    const isNewBuilding =
        (realtyObject?.__typename === 'ProfOfferFlat' && realtyObject.building?.isNewbuilding) ?? false;
    const isNewBuildingDeveloperFlat = isNewBuilding && model.seller.sellerType === 'DEVELOPER';
    const isPledge = (dealType?.__typename === 'ProfOfferDealSell' && dealType.hasBankPledge) ?? false;
    const price = priceCompat(dealType);
    const isSaleOffer = dealType?.__typename === 'ProfOfferDealSell';

    const offerRoutes = useOfferRouteListCompat(model);

    const {
        pictures,
        offerSnippetClickGtmId,
        hasAnyPriority,
        locatedInCenter,
        metersToCenter
    } = useOfferCompat(model);
    const addresses = useHandyAddressCompat({
        dealType: model.dealType,
        realtyObject: model.realtyObject,
        location: model.location,
        clickableAddress: model.location.clickableAddressShort,
        id: model.id
    });

    const { offerUrl } = useOfferCardLink(model);

    const { auth } = useM2AuthContext();
    const isMyOffer = Boolean(auth.user?.userId) && auth.user?.userId === model.seller.placementUserId;

    let eventAddId = gtmIdFavoriteClick;

    if (! eventAddId && dealType?.__typename === 'ProfOfferDealSell' && realtyObject?.__typename === 'ProfOfferFlat') {
        eventAddId = ClassifiedAnalytics.SearchCardSaleFlatAddFavorites;
    }

    const favorites = useFavoritesButtonState({
        forceFavoriteUpdate,
        id: model.id,
        noteTextInitial: model.noteText,
        isFavorite: model.isFavorite ?? false,
        eventAddId,
        organizationName: model.seller.organizationName
    });

    const offerRef = useRef<HTMLDivElement | null>(null);

    useOfferViewAnalytics({
        hasAnyPriority,
        isNewBuilding,
        offerId: model.id,
        offerRef: placement === 'modal' ? { current: null } : offerRef,
        isSaleOffer
    });
    const dataLayerPush = useClassifiedOfferDataLayerPush();

    const handleClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
        if (noLink) return;

        e.preventDefault();
        e.stopPropagation();

        if (placement === 'map') dataLayerPush({ event: 'up_offer_click', partner: model.fromCian ? 'cian' : 'unknown' });
        if (offerSnippetClickGtmId) {
            dataLayerPush({
                event: offerSnippetClickGtmId,
                offerId: model.id
            });
        }

        window.open(offerUrl, '_blank');
    }, [
        dataLayerPush,
        noLink,
        offerUrl,
        model,
        offerSnippetClickGtmId,
        placement
    ]);

    const [ isHover, setIsHover ] = useState(false);
    const hoverSnippet = () => setIsHover(true);
    const blurSnippet = () => setIsHover(false);

    const layoutImageIndex = pictures.findIndex(({ attributesType }) => attributesType === 'LAYOUT');

    let maxPicturesCount: number | undefined = hasAnyPriority ? 8 : 4;
    if (noLink) maxPicturesCount = undefined;

    const {
        isOpenSubscribeToPriceModal,
        openSubscribeToPriceModal,
        closeSubscribeToPriceModal
    } = useSubscribeToPriceModalState();

    const {
        modalName: modalNameSubscribeToPrice,
        subscriptionPriceEmail,
        setSubscriptionPriceEmail,
        subscribeToPrice,
        confirmEmail,
        otpSessionData,
        resetEmail,
        verifyEmail
    } = useSubscribeToPriceChange({
        closeSubscribeToPriceModal,
        openSubscribeToPriceModal,
        price: {
            value: price!.value,
            currency: price!.currency
        },
        offerId: model.id,
        setIsFavoriteCurrent: favorites.setIsFavorite
    });

    const getUrl = useClImageLazySrcSetUrl({ baseUrl: model?.gallery?.baseUrl });

    const { isVisited, enableIsVisited } = useOfferVisited(model);

    const sellerDescription = getAuthorDescription({
        ...model.seller,
        category: model.seller.sellerType
    });
    const firstOfferRoute = offerRoutes?.[0];

    return (<>
        <Paper
            className={cn(null, { hasHighlightPriority: model.status?.hasHighlight, pointer: placement === 'map' })}
            dataTest='offer'
            id={`offer-${model.id}`}
            ref={offerRef}
            onClick={handleClick}
            onMouseEnter={hoverSnippet}
            onMouseLeave={blurSnippet}
            borderRadius={SizeEnum.L}
        >
            <div className={cn('gallery')}>
                <ImageLazySrcSetProvide getUrl={getUrl}>
                    <GalleryMobile2
                        hovered={isHover}
                        isVisited={isVisited}
                        withNavButtons
                        maxPicturesCount={maxPicturesCount}
                        renderFavorite={! isMyOffer && model.isFavoriteVisible && model.isFavorite !== null ? (
                            ! favorites.isProfessional ? (
                                <FavoritesButton
                                    onClick={favorites.favoriteToggle}
                                    isActive={favorites.isFavorite}
                                    variant='primary-alt-bg'
                                    circle
                                    block
                                    shadow
                                />
                            ) : (
                                <AgentFavoritesModal
                                    isMyFavorite={favorites.isFavorite}
                                    offerId={model.id}
                                    onChangeMyFavorite={(v) => favorites.updateFavorite({
                                        isFavorite: v
                                    })}
                                    renderTrigger={({ handleToggle }) => (
                                        <FavoritesButton
                                            isActive={favorites.isFavorite}
                                            onClick={handleToggle}
                                            variant='primary-alt-bg'
                                            circle
                                            block
                                            shadow
                                        />
                                    )}
                                />
                            )
                        ) : undefined}
                        layoutImageIndex={layoutImageIndex}
                        layoutImageSrc={layoutImageIndex >= 0 ? pictures[layoutImageIndex].originPath : undefined}
                        pictures={pictures.map(({ originPath, description }) => ({
                            originPath,
                            description
                        }))}
                        renderBadges={<>
                            {isVisited && (
                                <DSBadge color='black' variant='light'>Просмотрено</DSBadge>
                            )}
                            {isNewBuilding && (
                                <DSBadge color='black'>{badgeLabel.newBuilding}</DSBadge>
                            )}
                            {isPledge && (
                                <DSBadge color='purple'>{badgeLabel.inPledge}</DSBadge>
                            )}
                        </>}
                    />
                </ImageLazySrcSetProvide>
            </div>
            <div className={cn('container')}>
                <a
                    className={cn('title', { visited: isVisited ?? false, active: ! noLink && isHover, noLink })}
                    onClick={(e) => {
                        if (noLink) return e.preventDefault();

                        enableIsVisited();
                    }}
                    href={offerUrl ?? ''}
                    target='_blank'
                >
                    {model.text?.titleSnippet}
                </a>
                {price && (
                    <Price
                        history={price.history ?? []}
                        variant='primary-alone'
                        priceValue={price.rubPrice ?? price.value}
                        priceCurrency={price.rubPrice ? Currency.RUB : price.currency}
                        rentPeriod={dealTypePeriodCompat(dealType)}
                        hasHighlight={model.status?.hasHighlight}
                        renderSubscribeToPrice={placement === 'map' ? (
                            <div onClick={e => e.stopPropagation()}>
                                <SubscribeToPriceChange
                                    offerId={model.id}
                                    onClose={closeSubscribeToPriceModal}
                                    subscriptionPriceEmail={subscriptionPriceEmail}
                                    setSubscriptionPriceEmail={setSubscriptionPriceEmail}
                                    handleSubmit={subscribeToPrice}
                                    confirmEmail={confirmEmail}
                                    hasSubscription={model.hasSubscription}
                                    formType='desktop-snippet'
                                />
                            </div>
                        ) : null}
                    />
                )}
                {realtyObject?.__typename === 'ProfOfferFlat' && realtyObject.building && (
                    <div>
                        {joinComponents(
                            realtyObject.building.newBuildingBasicInfo?.title,
                            realtyObject.building.newBuildingStatus?.builtYear && (<>
                                сдан,&nbsp;{realtyObject.building.newBuildingStatus.builtYear}&nbsp;г.
                            </>),
                            ! realtyObject.building.newBuildingStatus?.builtYear &&
                                realtyObject.building.newBuildingStatus?.formattedInProgress && (<>
                                    до&nbsp;{realtyObject.building.newBuildingStatus.formattedInProgress}</>)
                        )}
                    </div>
                )}
                <ClClickableAddress
                    noClickable
                    variant='secondary'
                    addresses={addresses}
                    metersToCenterText={locatedInCenter ? 'В центре' : (metersToCenter ? `до центра ${metersToCenter} км` : '')}
                    formattedAddressLong={model.location.formattedAddressLong ?? ''}
                />
                {firstOfferRoute && (<OfferRouteCard
                    noClickable
                    variant='secondary-alone'
                    key={firstOfferRoute.metro.id}
                    offerRoute={firstOfferRoute}
                />)}
                <div className={cn('description')}>
                    {sellerDescription ?? model.seller.name}
                </div>
            </div>

            <div className={cn('footer')}>
                {children}
            </div>
        </Paper>
        {isOpenSubscribeToPriceModal && (
            <SubscribeToPriceModal
                modalName={modalNameSubscribeToPrice}
                handleClose={closeSubscribeToPriceModal}
                email={subscriptionPriceEmail}
                otpSession={otpSessionData}
                onResetEmail={resetEmail}
                verifyEmail={verifyEmail}
            />
        )}
    </>);
}));
